import { supabase } from './supabase';
import { logger } from './logger';

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  name: string;
  type: 'candidate' | 'employer';
  isSeed: boolean;
  value: number;
  secondaryValue?: number;
  lastActivity?: string;
}

export interface InactiveUser {
  userId: string;
  name: string;
  type: 'candidate' | 'employer';
  email: string;
  isSeed: boolean;
  registeredAt: string;
  lastActivity: string | null;
  inactiveDays: number;
}

export interface TrendPoint {
  date: string;
  candidates: number;
  employers: number;
  seedCandidates: number;
  seedEmployers: number;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
}

export async function fetchVideoLeaderboard(params: {
  period: number;
  excludeSeeds: boolean;
}): Promise<LeaderboardEntry[]> {
  const since = params.period > 0 ? daysAgo(params.period) : undefined;

  let query = supabase
    .from('candidate_videos')
    .select('candidate_id, created_at');

  if (since) query = query.gte('created_at', since);

  const { data: videos, error } = await query;
  if (error) {
    logger.error('fetch_video_leaderboard', 'analyticsService', { error: error.message });
    throw error;
  }

  const countMap: Record<string, { count: number; last: string }> = {};
  (videos || []).forEach((v: any) => {
    const cid = v.candidate_id;
    if (!countMap[cid]) countMap[cid] = { count: 0, last: v.created_at };
    countMap[cid].count++;
    if (v.created_at > countMap[cid].last) countMap[cid].last = v.created_at;
  });

  const candidateIds = Object.keys(countMap);
  if (candidateIds.length === 0) return [];

  const { data: candidates } = await supabase
    .from('candidate_profiles')
    .select('id, user_id, first_name, last_name')
    .in('id', candidateIds);

  const userIds = (candidates || []).map((c: any) => c.user_id);
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, seed_tag')
    .in('id', userIds);

  const seedMap: Record<string, boolean> = {};
  (profiles || []).forEach((p: any) => { seedMap[p.id] = !!p.seed_tag; });

  const nameMap: Record<string, { name: string; userId: string }> = {};
  (candidates || []).forEach((c: any) => {
    nameMap[c.id] = { name: `${c.first_name || ''} ${c.last_name || ''}`.trim(), userId: c.user_id };
  });

  let entries = candidateIds
    .map((cid) => ({
      userId: nameMap[cid]?.userId || cid,
      name: nameMap[cid]?.name || 'Inconnu',
      type: 'candidate' as const,
      isSeed: seedMap[nameMap[cid]?.userId] || false,
      value: countMap[cid].count,
      lastActivity: new Date(countMap[cid].last).toLocaleDateString('fr-FR'),
    }))
    .filter((e) => !params.excludeSeeds || !e.isSeed)
    .sort((a, b) => b.value - a.value)
    .slice(0, 20);

  const result = entries.map((e, i) => ({ ...e, rank: i + 1 }));
  logger.technical('fetch_video_leaderboard_ok', 'analyticsService', { count: result.length });
  return result;
}

export async function fetchLikesReceivedLeaderboard(params: {
  period: number;
  excludeSeeds: boolean;
}): Promise<LeaderboardEntry[]> {
  const since = params.period > 0 ? daysAgo(params.period) : undefined;

  let query = supabase
    .from('video_interactions')
    .select('video_id, user_id, created_at')
    .eq('interaction', 'like');

  if (since) query = query.gte('created_at', since);

  const { data: likes, error } = await query;
  if (error) {
    logger.error('fetch_likes_leaderboard', 'analyticsService', { error: error.message });
    throw error;
  }

  const videoIds = [...new Set((likes || []).map((l: any) => l.video_id))];
  if (videoIds.length === 0) return [];

  const { data: videos } = await supabase
    .from('candidate_videos')
    .select('id, candidate_id')
    .in('id', videoIds);

  const videoOwner: Record<string, string> = {};
  (videos || []).forEach((v: any) => { videoOwner[v.id] = v.candidate_id; });

  const ownerLikes: Record<string, number> = {};
  (likes || []).forEach((l: any) => {
    const owner = videoOwner[l.video_id];
    if (owner) ownerLikes[owner] = (ownerLikes[owner] || 0) + 1;
  });

  const candidateIds = Object.keys(ownerLikes);
  const { data: candidates } = await supabase
    .from('candidate_profiles')
    .select('id, user_id, first_name, last_name')
    .in('id', candidateIds);

  const userIds = (candidates || []).map((c: any) => c.user_id);
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, seed_tag')
    .in('id', userIds);

  const seedMap: Record<string, boolean> = {};
  (profiles || []).forEach((p: any) => { seedMap[p.id] = !!p.seed_tag; });

  const nameMap: Record<string, { name: string; userId: string }> = {};
  (candidates || []).forEach((c: any) => {
    nameMap[c.id] = { name: `${c.first_name || ''} ${c.last_name || ''}`.trim(), userId: c.user_id };
  });

  let entries = candidateIds
    .map((cid) => ({
      userId: nameMap[cid]?.userId || cid,
      name: nameMap[cid]?.name || 'Inconnu',
      type: 'candidate' as const,
      isSeed: seedMap[nameMap[cid]?.userId] || false,
      value: ownerLikes[cid],
    }))
    .filter((e) => !params.excludeSeeds || !e.isSeed)
    .sort((a, b) => b.value - a.value)
    .slice(0, 20);

  const result2 = entries.map((e, i) => ({ ...e, rank: i + 1 }));
  logger.technical('fetch_likes_leaderboard_ok', 'analyticsService', { count: result2.length });
  return result2;
}

export async function fetchEngagementLeaderboard(params: {
  period: number;
  excludeSeeds: boolean;
}): Promise<(LeaderboardEntry & { likes: number; views: number; swipes: number; favorites: number })[]> {
  const since = params.period > 0 ? daysAgo(params.period) : undefined;

  let viQuery = supabase.from('video_interactions').select('user_id, interaction, created_at');
  if (since) viQuery = viQuery.gte('created_at', since);
  const { data: viData } = await viQuery;

  let swQuery = supabase.from('swipe_actions').select('user_id, created_at');
  if (since) swQuery = swQuery.gte('created_at', since);
  const { data: swData } = await swQuery;

  let favQuery = supabase.from('favorites').select('user_id, created_at');
  if (since) favQuery = favQuery.gte('created_at', since);
  const { data: favData } = await favQuery;

  const scores: Record<string, { likes: number; views: number; swipes: number; favorites: number }> = {};

  const ensure = (uid: string) => {
    if (!scores[uid]) scores[uid] = { likes: 0, views: 0, swipes: 0, favorites: 0 };
  };

  (viData || []).forEach((r: any) => {
    ensure(r.user_id);
    if (r.interaction === 'like') scores[r.user_id].likes++;
    else if (r.interaction === 'view') scores[r.user_id].views++;
  });
  (swData || []).forEach((r: any) => {
    ensure(r.user_id);
    scores[r.user_id].swipes++;
  });
  (favData || []).forEach((r: any) => {
    ensure(r.user_id);
    scores[r.user_id].favorites++;
  });

  const userIds = Object.keys(scores);
  if (userIds.length === 0) return [];

  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, role, seed_tag')
    .in('id', userIds);

  const profileMap: Record<string, any> = {};
  (profiles || []).forEach((p: any) => { profileMap[p.id] = p; });

  const { data: candidates } = await supabase
    .from('candidate_profiles')
    .select('user_id, first_name, last_name')
    .in('user_id', userIds);

  const { data: employers } = await supabase
    .from('employer_profiles')
    .select('user_id, first_name, last_name')
    .in('user_id', userIds);

  const nameMap: Record<string, string> = {};
  (candidates || []).forEach((c: any) => {
    nameMap[c.user_id] = `${c.first_name || ''} ${c.last_name || ''}`.trim();
  });
  (employers || []).forEach((e: any) => {
    nameMap[e.user_id] = `${e.first_name || ''} ${e.last_name || ''}`.trim();
  });

  let entries = userIds
    .map((uid) => {
      const s = scores[uid];
      const total = s.likes + s.views + s.swipes + s.favorites;
      return {
        userId: uid,
        name: nameMap[uid] || 'Inconnu',
        type: (profileMap[uid]?.role || 'candidate') as 'candidate' | 'employer',
        isSeed: !!profileMap[uid]?.seed_tag,
        value: total,
        likes: s.likes,
        views: s.views,
        swipes: s.swipes,
        favorites: s.favorites,
      };
    })
    .filter((e) => !params.excludeSeeds || !e.isSeed)
    .sort((a, b) => b.value - a.value)
    .slice(0, 20);

  const result3 = entries.map((e, i) => ({ ...e, rank: i + 1 }));
  logger.technical('fetch_engagement_leaderboard_ok', 'analyticsService', { count: result3.length });
  return result3;
}

export async function fetchInactiveUsers(params: {
  thresholdDays: number;
  userType: 'all' | 'candidate' | 'employer';
  excludeSeeds: boolean;
}): Promise<InactiveUser[]> {
  const threshold = daysAgo(params.thresholdDays);

  let query = supabase
    .from('profiles')
    .select('id, email, role, seed_tag, created_at, last_login_at');

  if (params.userType !== 'all') {
    query = query.eq('role', params.userType);
  }

  const { data: profiles, error } = await query;
  if (error) {
    logger.error('fetch_inactive_users', 'analyticsService', { error: error.message });
    throw error;
  }

  const allUserIds = (profiles || []).map((p: any) => p.id);

  const { data: viData } = await supabase
    .from('video_interactions')
    .select('user_id, created_at')
    .in('user_id', allUserIds)
    .order('created_at', { ascending: false });

  const { data: swData } = await supabase
    .from('swipe_actions')
    .select('user_id, created_at')
    .in('user_id', allUserIds)
    .order('created_at', { ascending: false });

  const lastActivityMap: Record<string, string> = {};
  const updateLast = (uid: string, date: string) => {
    if (!lastActivityMap[uid] || date > lastActivityMap[uid]) {
      lastActivityMap[uid] = date;
    }
  };
  (viData || []).forEach((r: any) => updateLast(r.user_id, r.created_at));
  (swData || []).forEach((r: any) => updateLast(r.user_id, r.created_at));

  const { data: candidates } = await supabase
    .from('candidate_profiles')
    .select('user_id, first_name, last_name')
    .in('user_id', allUserIds);

  const { data: employers } = await supabase
    .from('employer_profiles')
    .select('user_id, first_name, last_name')
    .in('user_id', allUserIds);

  const nameMap: Record<string, string> = {};
  (candidates || []).forEach((c: any) => {
    nameMap[c.user_id] = `${c.first_name || ''} ${c.last_name || ''}`.trim();
  });
  (employers || []).forEach((e: any) => {
    nameMap[e.user_id] = `${e.first_name || ''} ${e.last_name || ''}`.trim();
  });

  const now = Date.now();

  return (profiles || [])
    .map((p: any) => {
      const lastAct = lastActivityMap[p.id] || p.last_login_at || null;
      const lastDate = lastAct ? new Date(lastAct) : null;
      const inactiveDays = lastDate
        ? Math.floor((now - lastDate.getTime()) / (1000 * 60 * 60 * 24))
        : Math.floor((now - new Date(p.created_at).getTime()) / (1000 * 60 * 60 * 24));

      return {
        userId: p.id,
        name: nameMap[p.id] || p.email || 'Inconnu',
        type: p.role as 'candidate' | 'employer',
        email: p.email || '',
        isSeed: !!p.seed_tag,
        registeredAt: new Date(p.created_at).toLocaleDateString('fr-FR'),
        lastActivity: lastDate ? lastDate.toLocaleDateString('fr-FR') : null,
        inactiveDays,
      };
    })
    .filter((u) => u.inactiveDays >= params.thresholdDays)
    .filter((u) => !params.excludeSeeds || !u.isSeed)
    .sort((a, b) => b.inactiveDays - a.inactiveDays);
}

export async function fetchRegistrationTrends(periodDays: number): Promise<TrendPoint[]> {
  const since = daysAgo(periodDays);

  const { data: profiles, error } = await supabase
    .from('profiles')
    .select('role, seed_tag, created_at')
    .gte('created_at', since)
    .order('created_at', { ascending: true });

  if (error) {
    logger.error('fetch_registration_trends', 'analyticsService', { error: error.message });
    throw error;
  }

  const dayMap: Record<string, TrendPoint> = {};

  (profiles || []).forEach((p: any) => {
    const day = new Date(p.created_at).toISOString().slice(0, 10);
    if (!dayMap[day]) {
      dayMap[day] = { date: day, candidates: 0, employers: 0, seedCandidates: 0, seedEmployers: 0 };
    }
    const isSeed = !!p.seed_tag;
    if (p.role === 'candidate') {
      if (isSeed) dayMap[day].seedCandidates++;
      else dayMap[day].candidates++;
    } else if (p.role === 'employer') {
      if (isSeed) dayMap[day].seedEmployers++;
      else dayMap[day].employers++;
    }
  });

  const trendResult = Object.values(dayMap).sort((a, b) => a.date.localeCompare(b.date));
  logger.technical('fetch_registration_trends_ok', 'analyticsService', { days: trendResult.length });
  return trendResult;
}
